import React, { useCallback, useState } from 'react';
import { colors } from '@styleseat/brand';
import { Assignment } from '../../../../store/ABTest/ABTest.types';
import {
  BodyText, Stack, Separator, CheckButton,
} from '../../../ui';

import FlatList from '../../../ui/FlatList';
import Slider from '../../../ui/Slider';

export type TestAssignment = {
  name: string
} & Assignment;

type ABTestMenuItemProps = {
  assignment: TestAssignment;
  onValueChange: (testName: string, newValue: number) => void;
  onChoiceChange: (testName: string, newValue: boolean) => void;
};

const ABTestMenuItem: React.FC<ABTestMenuItemProps> = ({
  assignment,
  onValueChange,
  onChoiceChange,
}) => {
  const [currentValue, setCurrentValue] = useState<number>(assignment.allocationPercent);
  const [currentChoice, setCurrentChoice] = useState<boolean>(assignment.inTest);
  const handleValueChange = (newValue: string) => {
    onValueChange(assignment.name, Number(newValue));
    setCurrentValue(Number(newValue));
  };
  const handleChoiceChange = useCallback(() => {
    onChoiceChange(assignment.name, !currentChoice);
    setCurrentChoice(!currentChoice);
  }, [onChoiceChange, setCurrentChoice, currentChoice, assignment]);

  return (
    <Stack paddingY={12} paddingX={16}>
      <BodyText>{assignment.name} ({assignment.isEnabled ? 'Enabled' : 'Disabled'})</BodyText>
      <BodyText>{currentValue}</BodyText>
      {typeof assignment.allocationPercent === 'number' ? (
        <Slider
          onValueChange={handleValueChange}
          value={currentValue}
          minimumValue={0}
          maximumValue={1}
          step={0.01}
          className=""
          disabled={!assignment.isEnabled}
        />
      ) : (
        <CheckButton
          checked={currentChoice}
          testID="abtest-check"
          onClick={handleChoiceChange}
        >
          {currentChoice ? 'Test Group' : 'Control Group'}
        </CheckButton>
      )}
    </Stack>
  );
};

type ABTestMenuProps = {
  assignments: Array<TestAssignment>;
  onValueChange: ABTestMenuItemProps['onValueChange'];
  onChoiceChange: ABTestMenuItemProps['onChoiceChange'];
};
/**
 * Developer menu for updating AB test assignments in app
 */
export const ABTestMenu: React.FC<ABTestMenuProps> = ({
  assignments,
  onValueChange,
  onChoiceChange,
}) => (
  <FlatList
    data={assignments}
    itemHeight={88}
    renderItem={({ item }) => (
      <ABTestMenuItem
        assignment={item}
        onValueChange={onValueChange}
        onChoiceChange={onChoiceChange}
      />
    )}
    ItemSeparatorComponent={() => (
      <Separator appearance="extrathin" color={colors.silver} paddingY={0} paddingX={0} />
    )}
  />
);
